import { useEffect, useState, useRef, useCallback } from "react";
import { usePrivacyStore, PRIVACY_PATTERNS } from "@/stores/privacy-store";
import { X } from "lucide-react";

const MAX_MATCHES = 400;
const SCAN_INTERVAL_MS = 400;

// Clip `rect` to `bounds`; returns null when they don't overlap.
function clipRect(rect, bounds) {
  const left = Math.max(rect.left, bounds.left);
  const top = Math.max(rect.top, bounds.top);
  const right = Math.min(rect.right, bounds.right);
  const bottom = Math.min(rect.bottom, bounds.bottom);
  if (right <= left || bottom <= top) return null;
  return { left, top, width: right - left, height: bottom - top };
}

/**
 * PrivacyGuard — masks sensitive data so it never reaches the recording.
 *
 * The recording is a tab capture, so overlays painted here are exactly what the video
 * contains. Auto-detection reads DOM text (terminal, UI); it cannot see inside iframes
 * (PDF, Slides, embedded browser), which is what the manual zones are for.
 */
export default function PrivacyGuard({ containerRef }) {
  const {
    isEnabled,
    style,
    drawing,
    editingZones,
    autoPatterns,
    zones,
    addZone,
    removeZone,
    setDrawing,
  } = usePrivacyStore();

  const [autoRects, setAutoRects] = useState([]);
  const [containerRect, setContainerRect] = useState(null);
  const [dragStart, setDragStart] = useState(null);
  const [dragEnd, setDragEnd] = useState(null);
  const scanTimerRef = useRef(null);

  // ── Auto-detection: find sensitive text and measure it ──
  const scan = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;

    const bounds = container.getBoundingClientRect();
    setContainerRect(bounds);

    const activeKeys = Object.keys(autoPatterns).filter((k) => autoPatterns[k]);
    if (activeKeys.length === 0) {
      setAutoRects([]);
      return;
    }

    const found = [];
    const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT, {
      acceptNode(node) {
        if (!node.nodeValue || !node.nodeValue.trim()) return NodeFilter.FILTER_REJECT;
        const parent = node.parentElement;
        if (!parent || parent.closest("[data-privacy-overlay]")) return NodeFilter.FILTER_REJECT;
        return NodeFilter.FILTER_ACCEPT;
      },
    });

    let node;
    while ((node = walker.nextNode()) && found.length < MAX_MATCHES) {
      const text = node.nodeValue;
      // Keep masks inside their own panel: scrolled-out text still reports a rect
      const panel = node.parentElement?.closest("[data-panel-id]");
      const panelBounds = panel ? panel.getBoundingClientRect() : bounds;

      for (const key of activeKeys) {
        const regex = PRIVACY_PATTERNS[key].regex();
        let match;
        while ((match = regex.exec(text)) !== null && found.length < MAX_MATCHES) {
          if (match[0].length === 0) {
            regex.lastIndex++;
            continue;
          }
          // When the pattern captured a value (e.g. `password: hunter2`), mask only it
          const secret = match[1] !== undefined ? match[1] : match[0];
          const start = match[1] !== undefined ? match.index + match[0].indexOf(match[1]) : match.index;
          const end = start + secret.length;
          if (end > text.length) continue;

          try {
            const range = document.createRange();
            range.setStart(node, start);
            range.setEnd(node, end);
            for (const r of range.getClientRects()) {
              if (r.width <= 0 || r.height <= 0) continue;
              const clipped = clipRect(r, panelBounds);
              if (clipped) found.push(clipped);
              if (found.length >= MAX_MATCHES) break;
            }
          } catch {}
        }
      }
    }

    setAutoRects(found);
  }, [containerRef, autoPatterns]);

  useEffect(() => {
    if (!isEnabled) {
      setAutoRects([]);
      return;
    }
    scan();
    scanTimerRef.current = setInterval(scan, SCAN_INTERVAL_MS);
    const onChange = () => scan();
    window.addEventListener("resize", onChange);
    window.addEventListener("scroll", onChange, true);
    return () => {
      clearInterval(scanTimerRef.current);
      window.removeEventListener("resize", onChange);
      window.removeEventListener("scroll", onChange, true);
    };
  }, [isEnabled, scan]);

  // ── Manual zone drawing ──
  useEffect(() => {
    if (!drawing) {
      setDragStart(null);
      setDragEnd(null);
    }
  }, [drawing]);

  const maskStyle =
    style === "blur"
      ? { backdropFilter: "blur(14px)", WebkitBackdropFilter: "blur(14px)", background: "rgba(10,10,20,0.35)" }
      : { background: "#0b0b14" };

  if (!isEnabled) return null;

  const bounds = containerRect || containerRef.current?.getBoundingClientRect();
  if (!bounds) return null;

  const handleDown = (e) => {
    setDragStart({ x: e.clientX, y: e.clientY });
    setDragEnd({ x: e.clientX, y: e.clientY });
  };
  const handleMove = (e) => {
    if (dragStart) setDragEnd({ x: e.clientX, y: e.clientY });
  };
  const handleUp = () => {
    if (dragStart && dragEnd) {
      const w = Math.abs(dragEnd.x - dragStart.x);
      const h = Math.abs(dragEnd.y - dragStart.y);
      if (w > 8 && h > 8) {
        addZone({
          x: Math.min(dragStart.x, dragEnd.x) - bounds.left,
          y: Math.min(dragStart.y, dragEnd.y) - bounds.top,
          w,
          h,
        });
      }
    }
    setDragStart(null);
    setDragEnd(null);
    setDrawing(false);
  };

  return (
    <div data-privacy-overlay="true">
      {/* Auto-detected secrets */}
      {autoRects.map((r, i) => (
        <div
          key={`auto-${i}`}
          className="pointer-events-none fixed z-[9993] rounded-[3px]"
          style={{ left: r.left, top: r.top, width: r.width, height: r.height, ...maskStyle }}
        />
      ))}

      {/* Manual zones */}
      {zones.map((z) => (
        <div
          key={z.id}
          className="fixed z-[9993] rounded-md pointer-events-none"
          style={{ left: bounds.left + z.x, top: bounds.top + z.y, width: z.w, height: z.h, ...maskStyle }}
        >
          {editingZones && (
            <button
              onClick={() => removeZone(z.id)}
              className="pointer-events-auto absolute -top-2 -right-2 h-5 w-5 rounded-full bg-red-500 text-white flex items-center justify-center shadow-lg hover:bg-red-600"
              title="Supprimer cette zone"
            >
              <X className="h-3 w-3" />
            </button>
          )}
        </div>
      ))}

      {/* Capture layer while drawing a zone — also catches the mouse over iframes */}
      {drawing && (
        <div
          className="fixed z-[9994]"
          style={{
            left: bounds.left,
            top: bounds.top,
            width: bounds.width,
            height: bounds.height,
            cursor: "crosshair",
            background: "rgba(239,68,68,0.06)",
          }}
          onMouseDown={handleDown}
          onMouseMove={handleMove}
          onMouseUp={handleUp}
        >
          {dragStart && dragEnd && (
            <div
              className="absolute border-2 border-dashed border-red-400 bg-red-500/20 pointer-events-none"
              style={{
                left: Math.min(dragStart.x, dragEnd.x) - bounds.left,
                top: Math.min(dragStart.y, dragEnd.y) - bounds.top,
                width: Math.abs(dragEnd.x - dragStart.x),
                height: Math.abs(dragEnd.y - dragStart.y),
              }}
            />
          )}
        </div>
      )}
    </div>
  );
}
